import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import SmoothScrollProvider from "@/components/SmoothScrollProvider";

// Below-the-fold sections — lazy load to keep the initial JS bundle tiny.
const TrustBadges = lazy(() => import("@/components/TrustBadges"));
const About = lazy(() => import("@/components/About"));

// Heavy / desktop-only widgets — lazy load so mobile never pays the JS cost.
const Cursor3DEffect = lazy(() => import("@/components/Cursor3DEffect"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
const WebGLBackdrop = lazy(() => import("@/components/three/WebGLBackdrop"));
const ParallaxLayer = lazy(() => import("@/components/ParallaxLayer"));

// Defer below-the-fold sections to shrink the initial JS bundle and
// cut Total Blocking Time on desktop & mobile.
const Services = lazy(() => import("@/components/Services"));
const Projects = lazy(() => import("@/components/Projects"));
const Blog = lazy(() => import("@/components/Blog"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Contact = lazy(() => import("@/components/Contact"));
const Footer = lazy(() => import("@/components/Footer"));
const ScrollZoomReveal = lazy(() => import("@/components/ScrollZoomReveal"));

const LazySection = ({
  children,
  minHeight = "65vh",
}: {
  children: ReactNode;
  minHeight?: string;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      const id = window.setTimeout(() => setVisible(true), 3500);
      return () => window.clearTimeout(id);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px 0px", threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} style={!visible ? { minHeight } : undefined}>
      {visible ? <Suspense fallback={<div style={{ minHeight }} aria-hidden />}>{children}</Suspense> : null}
    </div>
  );
};

const Index = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsDesktop(
        window.innerWidth >= 1024 &&
          !window.matchMedia("(pointer: coarse)").matches
      );
    check();
    window.addEventListener("resize", check, { passive: true });
    // Defer non-critical widgets (WhatsApp button, cursor effect) until after
    // first interaction or idle so they never compete with FCP / LCP.
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const idle = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 2000));
    const id = idle(() => setDeferredReady(true));
    return () => {
      window.removeEventListener("resize", check);
      const cancel = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback ?? window.clearTimeout;
      cancel(id as number);
    };
  }, []);
  return (
    <SmoothScrollProvider>
        {isDesktop && deferredReady && (
          <Suspense fallback={null}>
            <Cursor3DEffect />
          </Suspense>
        )}
        <div data-accent="blue-amber" className="min-h-screen bg-card">
          <Navbar />
          <Hero />
          <LazySection minHeight="42vh"><TrustBadges /></LazySection>
          <LazySection><About /></LazySection>
          {/* Cinematic scroll zoom — desktop only to protect mobile perf */}
          {isDesktop && (
            <LazySection>
              <ScrollZoomReveal
                eyebrow="Scroll · Zoom · Depth"
                title="Step Inside"
                highlight="A Living Brand Experience"
                description="Layered scenes, scroll-driven zoom and parallax depth — every section is choreographed to feel like motion design, not a static page."
                accentA="270 95% 68%"
                accentB="32 95% 60%"
              />
            </LazySection>
          )}
          <LazySection minHeight="90vh">
            <div data-accent="violet" className="relative overflow-hidden">
              {isDesktop && (
                <>
                  <div className="absolute inset-0 opacity-50">
                    <WebGLBackdrop accentA="268 95% 68%" accentB="230 95% 70%" variant="ribbon" intensity={0.7} />
                  </div>
                  <ParallaxLayer
                    offset={120}
                    className="absolute -top-20 left-1/2 -translate-x-1/2 w-[80vw] h-[60vh] blur-3xl opacity-40 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at 50% 50%, hsla(268, 95%, 68%, 0.4) 0%, transparent 60%)",
                    }}
                  >
                    <span />
                  </ParallaxLayer>
                  <ParallaxLayer
                    offset={-80}
                    rotate={4}
                    className="absolute bottom-0 right-0 w-[40vw] h-[40vh] blur-3xl opacity-30 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle, hsla(32, 95%, 60%, 0.35) 0%, transparent 70%)",
                    }}
                  >
                    <span />
                  </ParallaxLayer>
                </>
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none" />
              <div className="relative">
                <Services />
              </div>
            </div>
          </LazySection>
          {isDesktop && (
            <LazySection>
              <ScrollZoomReveal
                eyebrow="Featured Outcomes"
                title="Real Brands."
                highlight="Real Revenue Lifts."
                description="Scroll through cinematic case studies — restaurants scaled with custom POS, Shopify stores re-engineered for conversion and ad campaigns engineered for ROAS."
                accentA="200 95% 68%"
                accentB="330 95% 68%"
              />
            </LazySection>
          )}
          <LazySection><Projects /></LazySection>
          <LazySection><Blog /></LazySection>
          <LazySection minHeight="50vh"><FAQ /></LazySection>
          <LazySection><Contact /></LazySection>
          <LazySection minHeight="24vh"><Footer /></LazySection>
        </div>
        {deferredReady && (
          <Suspense fallback={null}>
            <WhatsAppButton />
          </Suspense>
        )}
    </SmoothScrollProvider>
  );
};

export default Index;
